import React, { useEffect, useState } from 'react';
import Button from '@joinforma/ui/components/Button';
import Caption from '@joinforma/ui/components/Caption';
import styled from 'styled-components';
import { ButtonVariant } from '@joinforma/ui/constants/properties';
import { FormRenderProps } from 'react-final-form';
import { RenderMailingAddress } from './RenderMailingAddress';
import { RenderTextField } from './RenderTextField';
import { EditInfoFormProps } from './EditInfoForm';
import { FULL_NAME_MAX_LEN } from 'constants/validation';
import {
  isFirstNameValid,
  isLastNameValid,
  isEmailValid,
  isPhoneNumberValid,
  isPhoneNumberEmpty,
} from 'utils/validate';

const FIRST_NAME_FIELD_NAME = 'preferred_first_name';
const LAST_NAME_FIELD_NAME = 'preferred_last_name';

const FormStyled = styled.form`
  margin-top: 23px;
  display: flex;
  flex-direction: column;
`;

const CaptionStyled = styled(Caption)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
`;

const CaptionWithMarginStyled = styled(Caption)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.background.tertiary};
  padding-bottom: 14px;
  margin-bottom: 28px;

  color: ${({ theme }) => theme?.colors?.text?.secondary};
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-self: flex-end;
`;

const FormActionButtonStyled = styled(Button)`
  margin-left: 14px;
  margin-top: 63.5px;
  margin-bottom: 64.5px;
  align-self: flex-end;
`;

type RenderEditInfoFormProps = FormRenderProps &
  Pick<
    EditInfoFormProps,
    | 'is_primary_residence_present'
    | 'is_updating_employee'
    | 'mailing_address_description_placeholder'
    | 'primary_residence'
    | 'show_phone'
    | 'setShowEditForm'
  >;

export const RenderEditInfoForm = ({
  values,
  handleSubmit,
  form,
  is_primary_residence_present,
  is_updating_employee,
  mailing_address_description_placeholder,
  primary_residence,
  show_phone,
  setShowEditForm,
}: RenderEditInfoFormProps) => {
  const [combined_name_limit_error, setCombinedNameLimitError] = useState<
    string | null
  >(null);

  const is_combined_name_valid = () => combined_name_limit_error ?? undefined;

  const pref_first_name = values[FIRST_NAME_FIELD_NAME] ?? '';
  const pref_last_name = values[LAST_NAME_FIELD_NAME] ?? '';

  useEffect(() => {
    // Set the validator for combined name limit
    if (pref_first_name.length + pref_last_name.length > FULL_NAME_MAX_LEN) {
      setCombinedNameLimitError(
        `Your first and last name cannot be longer than ${FULL_NAME_MAX_LEN} characters total`
      );
    }
    // Reset validator for combined name
    else {
      setCombinedNameLimitError(null);
    }
  }, [pref_first_name, pref_last_name]);

  return (
    <FormStyled onSubmit={handleSubmit}>
      <RenderTextField
        name={FIRST_NAME_FIELD_NAME}
        label={'Preferred first name (optional)'}
        validators={[isFirstNameValid, is_combined_name_valid]}
      />
      <RenderTextField
        name={LAST_NAME_FIELD_NAME}
        label={'Preferred last name (optional)'}
        validators={[isLastNameValid, is_combined_name_valid]}
      />
      <CaptionWithMarginStyled>
        This is how your name will appear across the Forma member portal
      </CaptionWithMarginStyled>
      <RenderTextField
        name={'personal_email'}
        label={'Personal email address (optional)'}
        validators={[isEmailValid]}
      />
      <CaptionWithMarginStyled>
        We send important account information to your work email. If Forma is
        not able to contact you through your work email, you might receive
        important notices regarding your Forma access through your personal
        email.
      </CaptionWithMarginStyled>
      {mailing_address_description_placeholder && (
        <RenderMailingAddress
          mailing_address_description_placeholder={
            mailing_address_description_placeholder
          }
          is_primary_residence_present={is_primary_residence_present}
          onSameAsPrimaryClick={(same_as_primary_address: boolean) => {
            form.batch(() => {
              if (same_as_primary_address) {
                form.change('street', primary_residence?.street);
                form.change('locality', primary_residence?.locality);
                form.change('state', primary_residence?.state);
                form.change('zip', primary_residence?.zip);
                form.change('country', primary_residence?.country);
              }

              form.change('same_as_primary_address', same_as_primary_address);
            });
          }}
          onClearAddressClick={() => {
            form.batch(() => {
              form.change('street', '');
              form.change('locality', '');
              form.change('state', '');
              form.change('zip', '');
              form.change('country', '');
              form.change('same_as_primary_address', false);
            });
          }}
        />
      )}
      {show_phone && (
        <>
          <RenderTextField
            name="phone"
            label="Phone number"
            is_phone
            validators={[isPhoneNumberValid, isPhoneNumberEmpty]}
          />
          <CaptionStyled>
            We will only send texts for card transaction updates. If Forma is
            not able to contact you through emails, you may receive important
            notices regarding your Forma access through text messages.
          </CaptionStyled>
        </>
      )}
      <ActionButtonsContainer>
        <FormActionButtonStyled
          variant={ButtonVariant.SECONDARY}
          onClick={() => setShowEditForm(false)}
        >
          Back
        </FormActionButtonStyled>
        <FormActionButtonStyled
          loading={is_updating_employee}
          onClick={handleSubmit}
        >
          Save
        </FormActionButtonStyled>
      </ActionButtonsContainer>
    </FormStyled>
  );
};
